import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Text from '../Text';
import MagneticButton from '../animated/MagneticButton';
import { colors, gradients } from '../../theme/designTokens';

const NetworkCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    let animationId;
    let nodes = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.setTransform(window.devicePixelRatio, 0, 0, window.devicePixelRatio, 0, 0);
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      const count = Math.min(48, Math.floor((w * h) / 18000));
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
      }));
    };

    const draw = () => {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);
      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > w) n.vx *= -1;
        if (n.y < 0 || n.y > h) n.vy *= -1;
      });
      for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < nodes.length; j += 1) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 140) {
            ctx.strokeStyle = `rgba(148, 163, 184, ${0.16 * (1 - dist / 140)})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      nodes.forEach((n) => {
        ctx.fillStyle = 'rgba(203, 213, 225, 0.4)';
        ctx.beginPath();
        ctx.arc(n.x, n.y, 1.4, 0, Math.PI * 2);
        ctx.fill();
      });
      animationId = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <Box
      component="canvas"
      ref={canvasRef}
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        opacity: 0.65,
        pointerEvents: 'none',
      }}
    />
  );
};

const ResilienceHero = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: { xs: '92vh', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: gradients.hero,
        overflow: 'hidden',
        px: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <NetworkCanvas />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 50% 40%, rgba(15,40,80,0.25) 0%, rgba(2,8,24,0.78) 72%)',
          pointerEvents: 'none',
        }}
      />

      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        sx={{
          position: 'relative',
          zIndex: 2,
          textAlign: 'center',
          maxWidth: 960,
          py: { xs: 10, md: 12 },
        }}
      >
        <Text
          variant="eyebrow"
          sx={{
            color: colors.ochre,
            mb: 3,
            display: 'block',
            textTransform: 'none',
            letterSpacing: '0.18em',
            fontSize: { xs: '0.8rem', sm: '0.88rem', md: '0.95rem' },
          }}
        >
          ALEgION
          <Box
            component="sup"
            aria-label="registered"
            sx={{
              fontSize: '0.85em',
              ml: '0.14em',
              top: '-0.35em',
              position: 'relative',
              fontWeight: 700,
              letterSpacing: 0,
              fontFamily: '"DM Sans", "Inter", sans-serif',
            }}
          >
            ®
          </Box>
          <Box component="span" sx={{ mx: 1.25, opacity: 0.65, color: colors.ochreLight }}>
            ·
          </Box>
          <Box
            component="span"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.22em',
              color: colors.ochreLight,
            }}
          >
            Enterprise Resilience
          </Box>
        </Text>
        <Text variant="hero" color="white" sx={{ mb: 3 }}>
          Engineering Resilience.
        </Text>
        <Text
          variant="subtitle"
          sx={{
            maxWidth: 640,
            mx: 'auto',
            mb: 5,
            display: 'block',
            color: 'rgba(203, 213, 225, 0.78)',
          }}
        >
          Helping organizations anticipate uncertainty, absorb disruption, recover faster, and emerge stronger through integrated resilience, risk intelligence, insurance, and technology.
        </Text>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <MagneticButton
            variant="gold"
            component="a"
            href="https://erm.alegion.net/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Try ERM Efficiency Index
          </MagneticButton>
          <MagneticButton
            variant="outline"
            onClick={() => navigate('/framework')}
            sx={{
              color: colors.ochreLight,
              borderColor: 'rgba(212,160,23,0.55)',
              '&:hover': {
                borderColor: colors.ochreLight,
                color: colors.ochreLight,
                background: 'rgba(212,160,23,0.08)',
              },
            }}
          >
            Explore Our Framework
          </MagneticButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ResilienceHero;
